import { ArrowDown, ArrowUp, Bot, ChevronDown, ChevronRight, FileText, Play } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';

import {
  type ConsoleEvent,
  deepFormatJson,
  filterEvents,
  timestampTooltip,
} from '../utils/richTranscript';

interface EventLogProps {
  events: ConsoleEvent[];
  query: string;
  onPlayUserClip: (event: ConsoleEvent) => void;
  onSeek: (seconds: number) => void;
  onViewPrompt: (turnId: string) => void;
  canPlayUserClips: boolean;
  /** Call start (epoch ms), for the local/UTC time in each timestamp's tooltip. */
  startTime: number | null;
}

/** console-ui's source label: client blue, server green, agent plain; errors red, warnings orange. */
const SOURCE_STYLES: Record<string, string> = {
  client: 'text-primary',
  server: 'text-positive-foreground',
  agent: 'text-foreground',
};

const SOURCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  client: ArrowUp,
  server: ArrowDown,
  agent: Bot,
};

function sourceClass(event: ConsoleEvent): string {
  if (event.level === 'error') return 'text-destructive';
  if (event.level === 'warn') return 'text-attention-foreground';
  return SOURCE_STYLES[event.role] ?? 'text-muted-foreground';
}

const EventRow: React.FC<
  {
    event: ConsoleEvent;
    open: boolean;
    onToggle: () => void;
  } & Omit<EventLogProps, 'events' | 'query'>
> = ({
  event,
  open,
  onToggle,
  onPlayUserClip,
  onSeek,
  onViewPrompt,
  canPlayUserClips,
  startTime,
}) => {
  const Icon = SOURCE_ICONS[event.role];
  const payload = useMemo(
    () => (open ? JSON.stringify(deepFormatJson(event.data), null, 2) : ''),
    [open, event.data],
  );

  return (
    <li className="border-b border-border" data-testid="call-console-event">
      <div className="flex items-start gap-2 px-2 py-1 text-xs">
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 text-muted-foreground hover:text-foreground"
          aria-label={open ? 'Collapse event' : 'Expand event'}
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={() => onSeek(event.seconds)}
          className="shrink-0 text-left font-mono text-muted-foreground hover:font-semibold hover:text-foreground"
          title={`${timestampTooltip(event, startTime)}\nClick to seek the recording`}
        >
          {event.timestamp}
        </button>
        <span
          className={cn('flex w-16 shrink-0 items-center gap-0.5 font-bold', sourceClass(event))}
        >
          {Icon && <Icon className="h-3 w-3" />}
          {event.role}
        </span>
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 break-words text-left">
          {event.title}
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {event.userClip && canPlayUserClips && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1"
              onClick={() => onPlayUserClip(event)}
              aria-label="Play user utterance"
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
          {event.agentSpan && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1"
              onClick={() => onSeek(event.agentSpan!.start)}
              aria-label="Play agent speech"
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
          {event.turnId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1 text-xs"
              onClick={() => onViewPrompt(event.turnId!)}
            >
              <FileText className="mr-1 h-3 w-3" />
              View prompt
            </Button>
          )}
        </div>
      </div>
      {open && (
        <pre className="mx-2 mb-2 max-h-96 overflow-auto rounded bg-muted p-2 text-xs">
          {payload}
        </pre>
      )}
    </li>
  );
};

/** The call's agent events, newest last, filtered by the search query. */
export const EventLog: React.FC<EventLogProps> = ({ events, query, ...actions }) => {
  const [openRows, setOpenRows] = useState<Set<number>>(() => new Set());
  const visible = useMemo(() => filterEvents(events, query), [events, query]);

  const toggle = (index: number) =>
    setOpenRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  if (visible.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No events match.</p>;
  }

  return (
    <ul className="divide-y-0">
      {visible.map(event => (
        <EventRow
          key={event.index}
          event={event}
          open={openRows.has(event.index)}
          onToggle={() => toggle(event.index)}
          {...actions}
        />
      ))}
    </ul>
  );
};
